/**
 * Preloader Service
 *
 * Manages resource preloading based on flow configuration.
 * Supports three timing strategies:
 * - 'onload': Preload immediately when app starts
 * - 'afterAppLoaded': Preload after preloader hides (app is ready)
 * - 'screens-before': Preload N screens before target screen
 *
 * Supported resource types:
 * - 'images': Preload images from templates
 * - 'camera-detection': Preload ML models for face detection (TensorFlow)
 * - 'globe': Preload Globe component (Three.js)
 */

import type { FLOW_CONFIG } from '../config/flow';

interface PreloadConfig {
  when: 'onload' | 'afterAppLoaded' | { type: 'screens-before'; count: number };
  what: 'images' | 'camera-detection' | 'globe';
  images?: string[]; // Optional explicit image URLs (overrides auto-extraction)
}

interface PreloadTask {
  screenId: string;
  type: 'images' | 'camera-detection' | 'globe';
  resources: string[];
  triggered: boolean;
  when: PreloadConfig['when']; // Store timing config in task
}

export class PreloaderService {
  private tasks: Map<string, PreloadTask> = new Map();
  private loadedImages: Set<string> = new Set();
  private cameraDetectionLoaded: boolean = false;
  private globeLoaded: boolean = false;

  /**
   * Initialize the preloader by parsing flow configuration and creating tasks
   */
  initialize(flowConfig: typeof FLOW_CONFIG): void {
    const allScreens = flowConfig.getAllScreens();

    for (const screen of allScreens) {
      if (!screen.preload) continue;

      const preloadConfig = screen.preload as PreloadConfig;
      const task: PreloadTask = {
        screenId: screen.id,
        type: preloadConfig.what,
        resources: [],
        triggered: false,
        when: preloadConfig.when
      };

      // For images: use explicit list or auto-extract from template
      if (preloadConfig.what === 'images') {
        if (preloadConfig.images) {
          task.resources = preloadConfig.images;
        } else {
          task.resources = this.extractImagesFromTemplate(screen.template);
        }
      }

      this.tasks.set(screen.id, task);
      console.log(`[Preloader] Registered task for ${screen.id}:`, task);
    }
  }

  /**
   * Process all 'onload' preload tasks
   * Called once during app initialization
   */
  async processOnloadPreloads(): Promise<void> {
    const onloadTasks: [string, PreloadTask][] = [];

    // Find all onload tasks
    for (const [screenId, task] of this.tasks.entries()) {
      if (task.triggered) continue;

      // Check if this task has 'onload' timing
      if (task.when === 'onload') {
        onloadTasks.push([screenId, task]);
      }
    }

    // Process all onload tasks
    for (const [screenId, task] of onloadTasks) {
      console.log(`[Preloader] Processing onload for screen: ${screenId}`);

      if (task.type === 'images') {
        await this.preloadImages(task.resources);
      } else if (task.type === 'camera-detection') {
        await this.preloadCameraDetection();
      }

      task.triggered = true;
    }
  }

  /**
   * Check and trigger 'steps-before' preload tasks based on current screen
   * Called on every navigation
   */
  async checkStepsBeforePreloads(currentScreenId: string): Promise<void> {
    // Import FLOW_CONFIG here to avoid circular dependency
    const { FLOW_CONFIG } = await import('../config/flow.js');

    const currentIndex = FLOW_CONFIG.getScreenIndex(currentScreenId);

    for (const [screenId, task] of this.tasks.entries()) {
      if (task.triggered) continue;

      const screen = FLOW_CONFIG.getScreenById(screenId);
      if (!screen?.preload?.when || screen.preload.when === 'onload') continue;

      const preloadConfig = screen.preload as PreloadConfig;

      // Calculate if we should trigger this preload
      const targetIndex = FLOW_CONFIG.getScreenIndex(screenId);
      const stepsUntilTarget = targetIndex - currentIndex;

      if (typeof preloadConfig.when === 'object' && preloadConfig.when.type === 'screens-before') {
        const triggerDistance = preloadConfig.when.count;

        // Trigger when N screens or fewer away (but not if already passed or too far)
        if (stepsUntilTarget > 0 && stepsUntilTarget <= triggerDistance) {
          console.log(
            `[Preloader] Triggering ${screenId} preload (${stepsUntilTarget} screens away)`
          );

          // Trigger preload asynchronously (don't block navigation)
          if (task.type === 'images') {
            this.preloadImages(task.resources).catch(console.error);
          } else if (task.type === 'camera-detection') {
            this.preloadCameraDetection().catch(console.error);
          }

          task.triggered = true;
        }
      }
    }
  }

  /**
   * Extract image URLs from a screen template
   */
  private extractImagesFromTemplate(templateId: string): string[] {
    const template = document.getElementById(templateId);

    if (!template) {
      console.warn(`[Preloader] Template ${templateId} not found for image extraction`);
      return [];
    }

    const content = (template as HTMLTemplateElement).content;
    const images = content.querySelectorAll('img[src]');

    const urls = Array.from(images)
      .map((img) => img.getAttribute('src'))
      .filter(Boolean) as string[];

    console.log(`[Preloader] Extracted ${urls.length} images from ${templateId}:`, urls);
    return urls;
  }

  /**
   * Preload images using standard browser preloading
   */
  private async preloadImages(urls: string[]): Promise<void> {
    if (urls.length === 0) {
      console.log('[Preloader] No images to preload');
      return;
    }

    const promises = urls.map((url) => {
      // Skip if already loaded
      if (this.loadedImages.has(url)) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        const img = new Image();

        img.onload = () => {
          this.loadedImages.add(url);
          console.log(`[Preloader] ✓ Loaded: ${url}`);
          resolve();
        };

        img.onerror = () => {
          console.warn(`[Preloader] ✗ Failed to load: ${url}`);
          resolve(); // Fail gracefully
        };

        img.src = url;
      });
    });

    await Promise.all(promises);
    console.log(`[Preloader] Finished preloading ${urls.length} images`);
  }

  /**
   * Preload camera detection ML models
   */
  private async preloadCameraDetection(): Promise<void> {
    if (this.cameraDetectionLoaded) {
      console.log('[Preloader] Camera detection already loaded');
      return;
    }

    try {
      console.log('[Preloader] Starting camera detection preload...');
      const { preloadFaceLandmarks } = await import('../components/face-landmarks/adapter');
      await preloadFaceLandmarks();
      this.cameraDetectionLoaded = true;
      console.log('[Preloader] ✓ Camera detection models preloaded');
    } catch (error) {
      console.error('[Preloader] Failed to preload camera detection:', error);
      // Fail gracefully - models will load on-demand at camera screen
    }
  }

  /**
   * Preload Globe component (Three.js)
   */
  private async preloadGlobe(): Promise<void> {
    if (this.globeLoaded) {
      console.log('[Preloader] Globe already loaded');
      return;
    }

    try {
      console.log('[Preloader] Starting Globe preload...');
      const { initGlobe } = await import('../components/Globe');
      // Make initGlobe available globally for template usage
      (window as any).initGlobe = initGlobe;
      this.globeLoaded = true;
      console.log('[Preloader] ✓ Globe (Three.js) preloaded');
    } catch (error) {
      console.error('[Preloader] Failed to preload Globe:', error);
      // Fail gracefully - Globe will load on-demand when screen is shown
    }
  }

  /**
   * Process all 'afterAppLoaded' preload tasks
   * Called once after preloader hides and app is ready
   */
  async processAfterAppLoadedPreloads(): Promise<void> {
    const afterAppLoadedTasks: [string, PreloadTask][] = [];

    // Find all afterAppLoaded tasks
    for (const [screenId, task] of this.tasks.entries()) {
      if (task.triggered) continue;

      // Check if this task has 'afterAppLoaded' timing
      if (task.when === 'afterAppLoaded') {
        afterAppLoadedTasks.push([screenId, task]);
      }
    }

    // Process all afterAppLoaded tasks
    for (const [screenId, task] of afterAppLoadedTasks) {
      console.log(`[Preloader] Processing afterAppLoaded for screen: ${screenId}`);

      if (task.type === 'images') {
        await this.preloadImages(task.resources);
      } else if (task.type === 'camera-detection') {
        await this.preloadCameraDetection();
      } else if (task.type === 'globe') {
        await this.preloadGlobe();
      }

      task.triggered = true;
    }
  }

}
